import React from 'react'
import useAxios from 'axios-hooks'
import styled from 'styled-components'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Search,
} from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  Dropdown,
} from 'reactstrap'
import Select from '~/components/Select'

import { ReactComponent as EmptyBoxIcon } from '../assets/img/svg/empty-box.svg'
import { queries } from '~/constants/mediaQuery'
import QuestionCard from './QuestionCard'
import { useSelector } from 'react-redux'
import { useSearchParamsHandler } from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import { useSearch } from '~/hooks/useSearch'
import FilterButton from './FilterButton'
import DefaultFilterHandler from './Filters/Default/Handler'

const TAB_TO_STATUS = ['not_replied', 'replied', 'all']

const QuestionListing = () => {
  const intl = useIntl()

  const [filterOn, setFilterOn] = React.useState(false)
  const [tabId, setTabId] = React.useState(0)
  const { query, setQuery } = useSearchParamsHandler()

  const { search, setSearch } = useSearch()

  const [dropdownOpen, setDropdownOpen] = React.useState(false)

  const accountId = useSelector(state => state?.user?.profile?.account?.id)

  const filterCount = getDefaultFiltersCount({
    states: query.states,
    cities: query.cities,
    areas: query.areas,
    labels: query.labels,
    locations: query.locations,
  })

  const isMobile = window.innerWidth < 768

  const [{ data, loading }, fetchQuestions] = useAxios({
    method: 'POST',
    url: '/questions',
    data: {
      ...query,
      status: TAB_TO_STATUS[tabId],
    },
  })

  const [{ data: templates }] = useAxios(`/templates/questions/${accountId}`, {
    useCache: false,
  })

  const totalPages = React.useMemo(() => Math.ceil(data?.total / query.limit), [
    data?.total,
    query.limit,
  ])

  const pages = React.useMemo(() => {
    const page = query.page
    const MAX_PAGES_PAGINATION = isMobile ? 5 : 10
    const HALF_PAGES_PAGINATION = Math.ceil(MAX_PAGES_PAGINATION / 2)
    const pages = []
    for (let i = 1; i <= totalPages; i++) {
      const frontPageNum =
        page < HALF_PAGES_PAGINATION
          ? HALF_PAGES_PAGINATION + (HALF_PAGES_PAGINATION - page)
          : page > totalPages - (HALF_PAGES_PAGINATION - 1)
          ? totalPages - page + 1
          : HALF_PAGES_PAGINATION
      const backPageNum =
        page < HALF_PAGES_PAGINATION
          ? page
          : page > totalPages - (HALF_PAGES_PAGINATION - 1)
          ? MAX_PAGES_PAGINATION - 1 - (totalPages - page)
          : HALF_PAGES_PAGINATION

      if (i <= page - backPageNum) continue
      if (i >= page + frontPageNum) break
      pages.push(i)
    }
    return pages
  }, [totalPages, query.page, isMobile])

  function handlePaginate(page) {
    setQuery('page', page)
  }

  function handlePageNext() {
    if (query.page === totalPages) return
    const nextPage = query.page + 1
    setQuery('page', nextPage)
  }

  function handlePagePrev() {
    if (query.page === 1) return
    const prevPage = query.page - 1
    setQuery('page', prevPage)
  }

  function handleSearch(e) {
    setSearch(e.target.value)
  }

  function handleNavClick(id) {
    if (id === tabId) return
    const tabsNav = document.querySelector('.nav.nav-tabs')
    const tabs = tabsNav.childNodes
    tabs.forEach((tab, index) => {
      if (index === id) return tab.classList.add('active')
      tab.classList.remove('active')
    })

    setTabId(id)
  }

  function handleDropdownToggle() {
    setDropdownOpen(prevState => !prevState)
  }

  function handleDropdownClick(event) {
    const value = event.target.innerText
    setQuery('limit', Number(value))
    setDropdownOpen(false)
  }
  function handleDropdownOrderByClick(value) {
    setQuery('orderBy', value)
  }

  const options = {
    newest: {
      label: intl.formatMessage({ id: 'order-by.newest' }),
    },
    oldest: {
      label: intl.formatMessage({ id: 'order-by.oldest' }),
    },
  }
  return (
    <>
      <ListingHeader>
        <div>
          <h1>
            <FormattedMessage id="Questions" />
          </h1>

          <div>
            <div className="input-search">
              <Search color="#8D8D8D" size={20} className="search-icon" />
              <input
                value={search}
                onChange={handleSearch}
                type="text"
                placeholder={intl.formatMessage({ id: 'Search on table' })}
              />
            </div>
            <FilterButton
              isActive={filterOn}
              onClick={() => setFilterOn(prevState => !prevState)}
              filterCount={filterCount}
            />
          </div>
        </div>
        <DefaultFilterHandler
          open={filterOn}
          onClose={() => setFilterOn(false)}
        />

        <div className="w-100">
          <div className="reviews-tab">
            <StyledNav tabs>
              <NavItem className="active" onClick={() => handleNavClick(0)}>
                <FormattedMessage id="review-no-replies" />
              </NavItem>
              <NavItem onClick={() => handleNavClick(1)}>
                <FormattedMessage id="review-replied" />
              </NavItem>
              <NavItem onClick={() => handleNavClick(2)}>
                <FormattedMessage id="review-all" />
              </NavItem>
            </StyledNav>
          </div>
          <div className="count-and-order--wrapper">
            <div className="count-text">
              {data?.total || 0} <FormattedMessage id="questions" />
            </div>

            <Select
              id="order-by--select"
              label="Order by"
              value={
                query.orderBy
                  ? {
                      value: query.orderBy,
                      label: options[query.orderBy].label,
                    }
                  : undefined
              }
              placeholder={<FormattedMessage id="order-by.sort-by" />}
              onChange={(selected, helper) => {
                if (helper.action === 'clear') {
                  handleDropdownOrderByClick(undefined)
                } else {
                  handleDropdownOrderByClick(selected.value)
                }
              }}
              errorExtractor={error => error.value}
              options={Object.entries(options).map(([key, value]) => ({
                value: key,
                label: value.label,
              }))}
              menuPortalTarget={document.body}
              menuShouldScrollIntoView
              isClearable
            />
          </div>
        </div>
      </ListingHeader>
      <ListingBody>
        {Boolean(!data?.questions?.length && !loading) && (
          <div className="no-results">
            <div>
              <EmptyBoxIcon />
              <FormattedMessage
                id={
                  filterCount
                    ? 'No results found with filter'
                    : 'No questions found'
                }
              />
            </div>
          </div>
        )}
        {Boolean((data?.questions || [])?.length && !loading) && (
          <>
            {data.questions.map(question => (
              <QuestionCard
                key={question.id}
                question={question}
                fetchQuestions={fetchQuestions}
                templates={templates}
              />
            ))}
            <div className="review-pagination">
              <div className="d-flex flex-row justify-content-center align-items-center">
                <Dropdown
                  isOpen={dropdownOpen}
                  toggle={handleDropdownToggle}
                  defaultValue={query.limit}
                >
                  <DropdownToggle caret>
                    {query.limit}{' '}
                    {dropdownOpen ? (
                      <ChevronUp color="#A8A8A8" size="15" />
                    ) : (
                      <ChevronDown color="#A8A8A8" size="15" />
                    )}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={handleDropdownClick}>
                      10
                    </DropdownItem>
                    <DropdownItem onClick={handleDropdownClick}>
                      20
                    </DropdownItem>
                    <DropdownItem onClick={handleDropdownClick}>
                      30
                    </DropdownItem>
                    <DropdownItem onClick={handleDropdownClick}>
                      40
                    </DropdownItem>
                    <DropdownItem onClick={handleDropdownClick}>
                      50
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <div className="ml-2">
                  <FormattedMessage id={'per page'} />
                </div>
              </div>
              <CustomPagination>
                <button disabled={query.page === 1} onClick={handlePagePrev}>
                  <ChevronLeft
                    size="20"
                    color={query.page === 1 ? '#b3b1b1' : '#000'}
                  />
                </button>
                <div>
                  {pages.map(value => {
                    return (
                      <button
                        key={value}
                        onClick={() => handlePaginate(value)}
                        data-active={value === query.page}
                      >
                        {value}
                      </button>
                    )
                  })}
                </div>
                <button
                  disabled={query.page === Math.ceil(data?.total / query.limit)}
                  onClick={handlePageNext}
                >
                  <ChevronRight
                    size="20"
                    color={
                      query.page === Math.ceil(data?.total / query.limit)
                        ? '#b3b1b1'
                        : '#000'
                    }
                  />
                </button>
              </CustomPagination>
            </div>
          </>
        )}
        {loading && (
          <div className="loading-shimer">
            <div className="shimer-card"></div>
            <div className="shimer-card"></div>
            <div className="shimer-card"></div>
            <div className="shimer-pagination">
              <div></div>
              <div></div>
            </div>
          </div>
        )}
      </ListingBody>
    </>
  )
}

const ListingHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h1 {
    color: #767676;
    font-size: 1.5rem;
    font-weight: 600;
  }

  input {
    min-width: 300px;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    padding: 0.875rem 0.875rem 0.875rem 3rem;

    &:focus-visible {
      outline: #a6a4a4 auto 2px;
    }
  }

  button {
    height: 45px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 1rem;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    background-color: #fff;
    color: #2d3748;
    gap: 0.625rem;

    &:focus-visible {
      outline: #a6a4a4 auto 2px;
    }

    &:focus {
      outline: #a6a4a4 auto 2px;
    }

    &.view-button[data-active='true'] {
      border: none;
      background-color: #6f64f8;
      color: #fff;
    }
  }

  .input-search {
    position: relative;
    display: flex;
    justify-content: start;
    align-items: center;

    .search-icon {
      position: absolute;
      left: 1rem;
    }

    @media ${queries.less.sm} {
      display: none;
    }
  }

  > div {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;

    > div {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
    }

    .reviews-tab {
      @media ${queries.less.sm} {
        width: 100%;
      }
    }

    .count-text {
      color: #2f2b3de5;
      font-size: 0.875rem;
      font-weight: 600;

      @media ${queries.less.sm} {
        display: none;
      }
    }
  }

  @media ${queries.less.sm} {
    margin-top: 1rem;
  }

  .count-and-order--wrapper {
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    & #order-by--select {
      width: 235px;
    }
  }
`

const ListingBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 0.5rem;

  .dropdown-toggle {
    border: 1px solid #c4c4c4;
    background-color: #fff !important;
    color: #2c2c2c;

    &:focus {
      color: #2c2c2c !important;
    }

    &:focus-visible {
      color: #2c2c2c !important;
    }

    &:hover {
      color: #2c2c2c !important;
    }
  }

  .dropdown-item {
    width: 100% !important;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .review-pagination {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;

    @media ${queries.less.sm} {
      flex-direction: column-reverse;
      gap: 1rem;
      margin-bottom: 3rem;
    }
  }

  .no-results {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    padding: 2rem;

    div:nth-child(1) {
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #ffffff;
      font-size: 24px;
      color: #767676;
      border-radius: 0.5rem;
      border: 1px solid #e2e8f0;
      padding: 1rem;
      flex-direction: column;
      gap: 2rem;
      padding: 48px 24px;
      text-align: center;
      font-weight: 600;
      @media ${queries.more.sm} {
        height: 400px;
      }

      @media ${queries.less.sm} {
        padding: 24px;
      }
    }
  }

  .loading-shimer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    .shimer-card {
      width: 100%;
      height: 10rem;
      background-color: #f7fafc;
      border-radius: 0.5rem;
      animation: skeleton-loading 1s infinite alternate;
    }

    .shimer-pagination {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      @media ${queries.less.sm} {
        flex-direction: column-reverse;
        gap: 1rem;
      }

      div {
        height: 2.5rem;
        background-color: #f7fafc;
        border-radius: 0.5rem;
        animation: skeleton-loading 1s infinite alternate;

        &:nth-child(1) {
          width: 7rem;
        }

        &:nth-child(2) {
          width: 14rem;
        }
      }
    }

    @keyframes skeleton-loading {
      0% {
        background-color: hsl(200, 20%, 80%);
      }
      100% {
        background-color: hsl(200, 20%, 95%);
      }
    }
  }
`

const StyledNav = styled(Nav)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 2em;
  align-items: end;
  border-bottom: 1px solid rgba(196, 196, 196, 0.2) !important;
  padding: 0;
  box-shadow: none !important;
  overflow-x: auto;
  overflow-y: hidden;

  @media ${queries.less.sm} {
    width: 100%;
    gap: 0;
    justify-content: center;
    align-items: end;
  }

  > li {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: auto;
    height: 100%;
    font-size: 1rem;
    padding: 1em 2em 0.5em 2em;
    cursor: pointer;
    .nav-link {
      color: #d2d2ff !important;
    }

    @media ${queries.less.sm} {
      padding: 1em 1em 0.5em 1em;
    }
  }

  > li.active {
    color: #556ee6 !important;
    border-bottom: 3px solid #556ee6;
  }
`

const CustomPagination = styled.div`
  width: 80%;
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 1rem;

  @media ${queries.less.sm} {
    width: 100%;
    justify-content: center;
  }

  button {
    height: 2.5rem;
    width: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem;
    border: none;
    background-color: #ededed;
    border-radius: 999999px;
    aspect-ratio: 1/1;

    &:hover {
      background-color: #b3b1b1;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: none;
    }

    &[data-active='true'] {
      background-color: #6f64f8;
      color: #fff;
    }

    &:disabled {
      background-color: #f7fafc;
      cursor: not-allowed;
    }
  }

  div {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #ededed;
    border-radius: 999999px;
  }
`

export default QuestionListing
